import { Link } from 'react-router';
import {
  RUN_TYPE_LABELS,
  cashSummary,
  runProgress,
  splitRuns,
  type RunView,
} from '@ecobox/firebase';
import { Badge, Button, Card } from '@ecobox/ui';
import { useDriverData, useOptionalDriverData, type DriverData } from '../../hooks/useDriver';
import { useLedger } from '../../services';
import { Problems, Progress, fmt, runStatus } from './shared';

/** The driver's part of Today (05 §5.2): my runs with their progress, and the cash I hold. */
export function DriverToday() {
  const data = useOptionalDriverData();
  if (!data?.isDriver) return null;
  return <DriverTodayContent data={data} />;
}

function DriverTodayContent({ data }: { data: DriverData }) {
  const entries = useLedger();
  const { now, upcoming } = splitRuns(data.runs, data.today);

  // Cash by currency: what the server says I hold, plus cash taken on this phone and not yet synced.
  const held = new Map<string, { held: number; waiting: number }>();
  for (const c of data.custody) {
    const cur = held.get(c.currency) ?? { held: 0, waiting: 0 };
    held.set(c.currency, { ...cur, held: cur.held + c.heldMinor });
  }
  for (const run of data.runs) {
    const waiting = cashSummary({
      run,
      stops: data.stopsOf(run.id) ?? [],
      custody: [],
      entries,
    }).waitingMinor;
    if (waiting === 0) continue;
    const cur = held.get(run.currency) ?? { held: 0, waiting: 0 };
    held.set(run.currency, { ...cur, waiting: cur.waiting + waiting });
  }
  const allProblems = data.runs.flatMap((r) => data.problemsOf(r.id));

  return (
    <div className="ops-stack">
      <Card title="Cash I hold" data-tour="cash-held">
        {held.size === 0 ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            None. Cash you take at the door shows here until you hand it to the cashier.
          </p>
        ) : (
          <div className="ops-stack">
            {[...held].map(([currency, v]) => (
              <div key={currency}>
                <div className="ops-money" aria-label={`Cash held in ${currency}`}>
                  {fmt(v.held + v.waiting, currency)}
                </div>
                {v.waiting > 0 ? (
                  <div className="ops-muted">
                    includes {fmt(v.waiting, currency)} not synced yet
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Problems problems={allProblems} />
      {data.error ? (
        <p className="ops-muted" style={{ margin: 0 }}>
          Some of your runs could not be read right now. What is on the phone still works.
        </p>
      ) : null}

      <section className="ops-stack" aria-label="My runs today" data-tour="my-runs">
        <h2 className="ops-subtitle">My runs</h2>
        {now.length === 0 ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            {data.loading ? 'Loading your runs…' : 'No run for today. Check again later.'}
          </p>
        ) : (
          <ul className="ops-list" aria-label="Runs">
            {now.map((r) => (
              <RunCard key={r.id} run={r} today={data.today} />
            ))}
          </ul>
        )}
      </section>

      {upcoming.length > 0 ? (
        <section className="ops-stack" aria-label="Coming up">
          <h2 className="ops-subtitle">Coming up</h2>
          <ul className="ops-list" aria-label="Upcoming runs">
            {upcoming.map((r) => (
              <RunCard key={r.id} run={r} today={data.today} />
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function RunCard({ run, today }: { run: RunView; today: string }) {
  const data = useDriverData();
  const progress = runProgress(run, data.stopsOf(run.id));
  const status = runStatus(run.status);
  return (
    <li
      className="ops-row"
      data-tone={run.status === 'closed' ? 'ok' : run.status === 'active' ? 'waiting' : undefined}
    >
      <div className="ops-row__top">
        <strong>{RUN_TYPE_LABELS[run.type]}</strong>
        <Badge tone={run.waiting ? 'warning' : status.tone}>
          {run.waiting ? `${status.label} · waiting to sync` : status.label}
        </Badge>
      </div>
      <div className="ops-muted">
        {run.date === today ? 'Today' : run.date}
        {run.vehiclePlate ? ` · ${run.vehiclePlate}` : ''} · {run.branchId}
      </div>
      <Progress percent={progress.percent} label={`Progress of ${RUN_TYPE_LABELS[run.type]}`} />
      <div>
        {progress.done + progress.failed} of {progress.total} stops
        {progress.failed > 0 ? ` (${progress.failed} failed)` : ''}
        {run.counts.boxesOut > 0
          ? ` · ${run.counts.boxesDelivered} of ${run.counts.boxesOut} boxes`
          : ''}
        {run.cashCollectedMinor > 0 ? ` · cash ${fmt(run.cashCollectedMinor, run.currency)}` : ''}
      </div>
      <Link to={`/run/${run.id}`}>
        <Button size="touch" variant={run.status === 'closed' ? 'secondary' : 'cta'} block>
          {run.status === 'closed'
            ? 'See the run'
            : run.status === 'active'
              ? 'Continue the run'
              : 'Open the run'}
        </Button>
      </Link>
    </li>
  );
}
